import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/secure/v1/service.pb';
import { FaxMediaHooks, FaxMediaQueries } from '@frontend/api-fax-media';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { SelectImageModal } from '@frontend/select-image-modal';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  RTEditor,
  Button,
  useDebouncedFn,
  useAlert,
  useModalControl,
  useFormField,
  Modal,
} from '@frontend/design-system';
import { AssignLocationAccessModal } from '../components/templates/assign-location-access-modal';
import { SaveTemplateModal } from '../components/templates/save-template-modal';
import { defaultHTML, dynamicFields } from '../constants';

const fieldValues = Object.values(dynamicFields);

export const FaxTemplateEditor = () => {
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const { selectedLocationId, templateId } =
    useSearch({
      from: '/fax/drafts/template-editor',
      shouldThrow: false,
    }) ?? {};

  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];

  const navigate = useNavigate();
  const invalidateFaxTemplateList = FaxMediaHooks.useInvalidateFaxTemplatesList();
  const [currHTML, setCurrHTML] = useState<string>();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const [locationsWithAccess, setLocationsWithAccess] = useState<string[]>(locationId ? [locationId] : []);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const { modalProps: saveTemplateModalProps, triggerProps: saveTemplateTriggerProps } = useModalControl();
  const { modalProps: locationAccessModalProps, triggerProps: locationAccessTriggerProps } = useModalControl();
  const { modalProps: addImageModalProps, triggerProps: addImageTriggerProps } = useModalControl();
  const nameFieldProps = useFormField({ type: 'text', placeholder: t('New Template Title'), value: '' }, []);

  const user = getUser();

  const currentTime = new Date().toISOString();

  const invalidateFaxTemplate = FaxMediaQueries.useInvalidateCoversheetTemplate();
  const { mutateAsync: upsertCoversheetTemplate, isLoading: upsertCoversheetTemplateIsLoading } =
    FaxMediaQueries.useUpsertCoversheetTemplate({
      onSuccess: ({ id: templateId }) => {
        invalidateFaxTemplateList();
        if (templateId && locationId) invalidateFaxTemplate(templateId, locationId);
        alert.success({
          message: t('Template edited successfully.'),
        });
      },
      onError: () => {
        alert.error({
          message: t('Error editing template.'),
        });
      },
    });

  const { data: template } = FaxMediaQueries.useGetCoversheetTemplate(templateId ?? '', locationId ?? '', {
    enabled: !!templateId && !!locationId,
    onSuccess: (data) => {
      setCurrHTML(data.html);
    },
  });

  const { mutateAsync: renderCoversheet, isLoading: renderCoversheetIsLoading } = FaxMediaQueries.useRenderCoversheet(
    {}
  );

  const onEditorChange = useDebouncedFn((html: string) => {
    setCurrHTML(html);
  }, 250);

  const handleSaveTemplate = async () => {
    const renderedCoversheet = await renderCoversheet({
      locationId,
      html: currHTML,
      populateDynamicFields: false,
    });

    await upsertCoversheetTemplate({
      locationId: locationId ?? '',
      coversheetTemplate: {
        id: templateId,
        name: template?.name || '',
        locationIds: template?.locationIds ?? [],
        creatorLocationId: template?.creatorLocationId ?? '',
        html: currHTML,
        createdAt: template?.createdAt ?? currentTime,
        updatedAt: currentTime,
        userId: user?.userID || '',
        attributes: renderedCoversheet.attributes || {
          pages: 1,
          size: 5,
        },
      },
    });

    back();
  };

  const back = () => navigate({ to: '/fax/drafts/new', search: { selectedLocationId: locationId } });

  useEffect(() => {
    setCurrHTML(template?.html || defaultHTML);
  }, [template?.html]);

  return (
    <section
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        className='editor'
        css={css`
          padding: ${isMobile ? '0' : theme.spacing(2)};
          background: ${theme.colors.neutral5};
          height: 100%;
          overflow: scroll;
          .editor-container {
            margin-top: 0 !important;
            background: transparent !important;
          }
          .editor-inner {
            background: transparent !important;
            height: 100%;
          }
          .editor-scroller {
            height: 100%;
            overflow: hidden;
          }
          .toolbar-container {
            justify-content: center;
            position: sticky;
            top: 0;
          }

          .editor-input {
            height: 100%;
          }
        `}
      >
        <RTEditor
          key={template?.id ? template?.id : 'new-template'}
          uniqueId={template?.id ? template?.id : 'new-template'}
          initialHTML={template?.html || defaultHTML}
          onHTMLChange={onEditorChange}
        >
          <RTEditor.Toolbar>
            <RTEditor.Toolbar.History />
            <RTEditor.Toolbar.FontPicker />
            <RTEditor.Toolbar.FontSize />
            <RTEditor.Toolbar.BlockType />
            <RTEditor.Toolbar.Bold />
            <RTEditor.Toolbar.Italic />
            <RTEditor.Toolbar.Underline />
            <RTEditor.Toolbar.Image
              onClick={() => {
                addImageTriggerProps.onClick();
              }}
              src={selectedImage}
              setSrc={setSelectedImage}
            />
            <RTEditor.Toolbar.FontColor />
            <RTEditor.Toolbar.ElementFormat />
            <RTEditor.Toolbar.DynamicField fields={fieldValues} />
          </RTEditor.Toolbar>

          <div
            className='fax-page'
            css={css`
              aspect-ratio: 8.5/11;
              width: ${isMobile ? '100%' : '612px'}; // 8.5in
              height: 792px; // 11in
              overflow-y: scroll;
              background: ${theme.colors.white};
              margin: auto;
              margin-top: ${theme.spacing(2)};
              margin-bottom: ${theme.spacing(2)};
              border-radius: ${isMobile ? '0' : theme.borderRadius.medium};
              border: 1px solid ${theme.colors.neutral20};
              box-shadow: ${theme.shadows.light};
              box-sizing: border-box;
              padding: ${theme.spacing(2)};
            `}
          >
            <RTEditor.Editor />
          </div>
        </RTEditor>
      </div>
      <footer
        css={{
          display: 'flex',
          flexDirection: isMobile ? 'column-reverse' : 'row',
          justifyContent: 'space-between',
          background: theme.colors.white,
          padding: theme.spacing(3),
          borderTop: `1px solid ${theme.colors.neutral20}`,
          paddingBottom: isMobile ? 0 : theme.spacing(3),
          trasition: 'padding-bottom 300ms ease-in-out',
        }}
      >
        <Button onClick={back} size='large' variant='secondary'>
          {t('Back')}
        </Button>
        <div
          css={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
        >
          <Button
            onClick={
              !!templateId
                ? handleSaveTemplate
                : locationId && selectedLocationIds.length > 1
                ? locationAccessTriggerProps.onClick
                : saveTemplateTriggerProps.onClick
            }
            size='large'
            css={{
              width: 'auto',
              marginBottom: isMobile ? theme.spacing(2) : '0',
              marginRight: isMobile ? '0' : theme.spacing(2),
            }}
            variant='primary'
            loading={upsertCoversheetTemplateIsLoading || renderCoversheetIsLoading}
          >
            {t('Save Template')}
          </Button>
        </div>
      </footer>
      {locationId && selectedLocationIds.length > 1 && (
        <AssignLocationAccessModal
          modalProps={locationAccessModalProps}
          onSave={(locations) => {
            setLocationsWithAccess(locations);
            locationAccessModalProps.onClose();
            saveTemplateTriggerProps.onClick();
          }}
          locationsWithAccess={locationsWithAccess}
          mode='progression'
        />
      )}
      <SaveTemplateModal
        fieldProps={nameFieldProps}
        modalProps={saveTemplateModalProps}
        locationId={locationId ?? ''}
        locationsWithAccess={locationsWithAccess}
        html={currHTML}
        isCreateTemplate={!templateId}
      />
      <Modal {...addImageModalProps} minWidth={700}>
        <SelectImageModal
          locationIds={[locationId]}
          modalProps={addImageModalProps}
          onSelect={(media?: Media) => {
            if (media?.url) {
              setSelectedImage(media?.url);
            }
            addImageModalProps.onClose();
          }}
          showLocationFilter
        />
      </Modal>
    </section>
  );
};
